import { find } from 'lodash/fp';
import { Resource } from '@mews-commander/resources';
import { alpha, getToken } from '@optimus-web/core';
import { DENSE_B2B_LIGHT_THEME } from '@optimus-web/theme';
import { COLOR_TEXT, COLOR_WHITE, COLOR_TRANSPARENT } from '../../../components/commander-ui/constants';
import {
    COLOR_HIGHLIGHT,
    FONT_FAMILY,
    FONT_SIZE,
    LINE_HEIGHT,
    PI_2,
    DATE_LEGEND_HEIGHT,
    TEXT_PADDING,
    EVENT_RADIUS,
    EVENT_PADDING,
    EVENT_FONT_SIZE,
    RESOURCE_CATEGORY_TYPE_FONT_SIZE,
    SECOND_LINE,
    EVENT_BORDER_RADIUS,
    RESOURCE_WIDTH,
    COLOR_HIGHLIGHT_SELECTED,
} from '../constants';
import { Coordinates } from '../types';
import { getCenter } from './mathUtils';
import svgrToPaths from './svgrToPaths';

export function drawText(
    ctx: $TSFixMe,
    text: $TSFixMe,
    x: $TSFixMe,
    y: $TSFixMe,
    bold = false,
    fontSize = FONT_SIZE,
    color = COLOR_TEXT
) {
    ctx.fillStyle = color;
    ctx.font = `${bold ? '600' : ''} ${fontSize}px ${FONT_FAMILY}`;
    ctx.fillText(text, x, y);
}

export function drawVerticalText(ctx: $TSFixMe, x2: $TSFixMe, y1: $TSFixMe, y2: $TSFixMe, label: $TSFixMe) {
    ctx.save();
    ctx.fillStyle = COLOR_TEXT;
    ctx.translate(x2, getCenter(y1, y2));
    ctx.rotate(-90 * (Math.PI / 180));
    ctx.fillText(label, -ctx.measureText(label).width * 0.5, -9);
    ctx.restore();
}

export function drawIcon(ctx: $TSFixMe, x: $TSFixMe, y: $TSFixMe, icon: $TSFixMe, scale: number, color: $TSFixMe) {
    ctx.save();

    const finalIcon = icon?.component || icon || null;

    const iconElement = typeof finalIcon === 'function' ? finalIcon() : finalIcon;
    const paths = svgrToPaths(iconElement);

    ctx.translate(x, y);
    ctx.fillStyle = color;
    ctx.scale(scale, scale);

    paths.forEach((item) => {
        const path = new Path2D(item);
        ctx.fill(path);
    });

    ctx.restore();
}

export function drawClassification(
    ctx: $TSFixMe,
    x: $TSFixMe,
    y: $TSFixMe,
    width: $TSFixMe,
    padding: $TSFixMe,
    icon: $TSFixMe,
    iconSize: $TSFixMe,
    textColor: $TSFixMe,
    scale: number
) {
    drawIcon(ctx, x - width + (width - iconSize) / 2, y, icon, scale, textColor);
}

export function drawType({ ctx, yAxis, height, type, width, isHighlighted = false, withLabel = true }: $TSFixMe) {
    const startY = Math.max((yAxis(type.minY) as number) + DATE_LEGEND_HEIGHT, LINE_HEIGHT - DATE_LEGEND_HEIGHT);
    const endY = Math.min((yAxis(type.maxY) as number) + DATE_LEGEND_HEIGHT, height);

    drawSpaceRectangle(
        ctx,
        0,
        width,
        startY,
        endY,
        true,
        COLOR_TRANSPARENT,
        getToken('paletteBrandNight200')({ theme: DENSE_B2B_LIGHT_THEME })
    );

    if (isHighlighted) {
        ctx.fillStyle = COLOR_HIGHLIGHT;
        ctx.fillRect(0, startY, LINE_HEIGHT, endY - startY);
    }

    if (withLabel) {
        ctx.font = `${RESOURCE_CATEGORY_TYPE_FONT_SIZE}px ${FONT_FAMILY}`;
        const textSpace = endY - TEXT_PADDING - (startY + TEXT_PADDING);
        drawVerticalText(ctx, LINE_HEIGHT, startY, endY, fitResourceCategoryTypeLabel(ctx, type, textSpace));
    }
}

export function drawDayRectangle(ctx: $TSFixMe, x1: $TSFixMe, width: $TSFixMe, height: $TSFixMe, color = COLOR_WHITE) {
    ctx.fillStyle = color;
    ctx.fillRect(x1, LINE_HEIGHT, width, height);

    line(ctx, x1, LINE_HEIGHT, x1, height * 2);
}

export function drawEvents(ctx: $TSFixMe, x: $TSFixMe, count: $TSFixMe) {
    const circleY = SECOND_LINE - EVENT_PADDING / 2;
    const label = formatCountToMaxNine(count);
    drawCircle(
        ctx,
        x - EVENT_PADDING - EVENT_RADIUS,
        circleY - EVENT_RADIUS + EVENT_PADDING,
        EVENT_BORDER_RADIUS,
        COLOR_WHITE
    );
    drawCircle(
        ctx,
        x - EVENT_PADDING - EVENT_RADIUS,
        circleY - EVENT_RADIUS + EVENT_PADDING,
        EVENT_RADIUS,
        getToken('backgroundAccentPrimary')({ theme: DENSE_B2B_LIGHT_THEME })
    );
    ctx.textAlign = 'center';
    drawText(ctx, label, x - EVENT_RADIUS - EVENT_PADDING, SECOND_LINE, true, EVENT_FONT_SIZE, COLOR_WHITE);
    ctx.textAlign = 'left';
}

export function drawSpaceRectangle(
    ctx: $TSFixMe,
    x1: $TSFixMe,
    x2: $TSFixMe,
    y1: $TSFixMe,
    y2: $TSFixMe,
    withLines: $TSFixMe,
    fillColor = COLOR_WHITE,
    borderColor?: $TSFixMe
) {
    if (withLines) {
        line(ctx, x1, y1, x2, y1, borderColor);
        line(ctx, x1, y2, x2, y2, borderColor);
    }
    ctx.fillStyle = fillColor;
    ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
}

export function drawUseRectangle({
    ctx,
    x1,
    x2,
    y1,
    y2,
    radius,
    bgColor,
    strokeColor,
    dashed,
    bgAlpha,
}: Coordinates & {
    ctx: CanvasRenderingContext2D;
    radius: number;
    bgColor: string;
    strokeColor: string;
    dashed: boolean;
    bgAlpha?: number;
}) {
    ctx.fillStyle = bgColor;
    ctx.strokeStyle = strokeColor;

    drawRoundRect(ctx, x1, y1, x2 - x1, y2 - y1, radius);

    const originalAlpha = ctx.globalAlpha;
    ctx.globalAlpha = bgAlpha ?? originalAlpha;
    ctx.fill();
    ctx.globalAlpha = originalAlpha;

    if (dashed) {
        ctx.setLineDash([2, 3]);
    }
    ctx.stroke();
    if (dashed) {
        ctx.setLineDash([]);
    }
}

function drawRoundRect(ctx: $TSFixMe, x: number, y: number, width: number, height: number, radius: number) {
    const r = Math.min(radius, Math.min(width, height) / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

export function line(c: $TSFixMe, x1: $TSFixMe, y1: $TSFixMe, x2: $TSFixMe, y2: $TSFixMe, color?: $TSFixMe) {
    c.beginPath();
    c.moveTo(Math.floor(x1) + 0.5, Math.floor(y1) + 0.5);
    c.lineTo(Math.floor(x2) + 0.5, Math.floor(y2) + 0.5);
    c.lineWidth = 1;
    c.strokeStyle =
        color ||
        alpha(
            getToken('paletteBrandNight150')({ theme: DENSE_B2B_LIGHT_THEME }),
            getToken('opacity400')({ theme: DENSE_B2B_LIGHT_THEME })
        );
    c.stroke();
}

export function getSpaceRectangle(space: $TSFixMe, spaceWidth: $TSFixMe, yAxis: $TSFixMe) {
    return {
        x1: space.minX * spaceWidth + LINE_HEIGHT,
        x2: space.maxX * spaceWidth + LINE_HEIGHT,
        y1: yAxis(space.minY) as number,
        y2: yAxis(space.maxY) as number,
    };
}

export function getSpaceBackground({ id, isSlot }: $TSFixMe, highlightedSpaces: $TSFixMe, searchedSpace?: Resource) {
    if (isSlot) {
        return alpha(
            getToken('paletteSemanticRed600')({ theme: DENSE_B2B_LIGHT_THEME }),
            getToken('opacity200')({ theme: DENSE_B2B_LIGHT_THEME })
        );
    }
    if (id === searchedSpace?.id) {
        return COLOR_HIGHLIGHT_SELECTED;
    }
    if (find({ id }, highlightedSpaces)) {
        return COLOR_HIGHLIGHT;
    }

    return COLOR_TRANSPARENT;
}

export function drawCircle(ctx: $TSFixMe, x: $TSFixMe, y: $TSFixMe, r: $TSFixMe, color: $TSFixMe) {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, PI_2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.closePath();
}

function fitResourceCategoryTypeLabel(ctx: $TSFixMe, type: $TSFixMe, textSpace: $TSFixMe) {
    if (textFits(ctx, type.resourceCategoryName, textSpace)) {
        return type.resourceCategoryName;
    }

    if (textFits(ctx, type.resourceCategoryShortName, textSpace)) {
        return type.resourceCategoryShortName;
    }

    const firstLetter = type.resourceCategoryShortName.substr(0, 1);
    if (textFits(ctx, firstLetter, textSpace)) {
        return firstLetter;
    }

    return '';
}

export function fitText(context: $TSFixMe, text = '', width: $TSFixMe) {
    const binaryFit = (min: number, pos: number, max: number): { text: string; width: number } => {
        const fitted = text.substring(0, pos);
        if (min === max) {
            return { text: fitted, width: context.measureText(fitted).width };
        } else if (textFits(context, fitted, width)) {
            return binaryFit(pos, max - Math.floor((max - pos) / 2), max);
        }

        return binaryFit(min, min + Math.floor((pos - min) / 2), Math.min(pos, max - 1));
    };

    return binaryFit(0, text.length, text.length);
}

export function textFits(context: $TSFixMe, text: $TSFixMe, width: $TSFixMe) {
    return context.measureText(text).width <= width;
}

export function getResourceLegendWidth(model: $TSFixMe, scale: number, withResourceLegend: boolean) {
    const legendWidth = RESOURCE_WIDTH * ((model.getMaxResourceDepth() as number) + 1) + LINE_HEIGHT;
    return withResourceLegend ? legendWidth : 0;
}

function formatCountToMaxNine(count: number) {
    return count > 9 ? '9+' : `${count}`;
}
